import { useTranslation } from "react-i18next";
import * as API from "lib/api/types";
import routes from "routes";

export interface DashboardViewModel {
  billingName: string;
  billingDetails: string[];
  chapter: string;
  duesDetail: string;
  email: string;
  cardExpiration: string | null;
  joined: string | null;
  mailingDetails: string[];
  memberStatus: API.MemberStatus;
  mergedAccountMessage: string;
  membershipIsMerged: boolean;
  membershipProofActionHref: string | null;
  membershipStatus: string;
  membershipStatusIcon: "check" | "exclamation" | null;
  membershipStatusIsGoodStanding: boolean;
  name: string;
  nextPaymentDate: string | null;
  paymentMethod: string | null;
  phone: string | null;
  profileActionLabel: string;
  profileImageAlt?: string;
  profileImageSrc?: string | null;
  proofActionLabel: string;
}

export function displayName(profile: API.UserProfile): string {
  const chosen = [profile.chosenFirstName, profile.chosenLastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  if (chosen) return chosen;

  return [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim();
}

export function addressLines(address?: API.Address): string[] {
  if (!address) return [];
  const lines = [address.line1, address.line2]
    .filter(Boolean)
    .filter((line, index, values) => values.indexOf(line) === index);
  const cityStateZip = [address.city, address.state, address.zip]
    .filter(Boolean)
    .join(", ");

  return [...lines, cityStateZip].filter(Boolean);
}

function formatMonthYear(value: string, locale: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

function formatLongDate(value: string, locale: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function mobilePhoneNumber(profile: API.UserProfile): string | null {
  if (!Array.isArray(profile.phones)) return null;

  return profile.phones.find((phone) => phone.type === "mobile")?.number ?? null;
}

export function useDashboardViewModel(profile: API.UserProfile): DashboardViewModel {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const joinedDate = profile.joinDate
    ? formatMonthYear(profile.joinDate, language)
    : null;
  const name = displayName(profile);
  const membershipStatusIsGoodStanding =
    profile.memberStatus === API.MemberStatus.MEMBER;
  const membershipStatusIcon = membershipStatusIsGoodStanding
    ? "check"
    : profile.memberStatus === API.MemberStatus.LAPSED_MEMBER
      ? "exclamation"
      : null;
  const membershipIsRenewable =
    profile.memberStatus === API.MemberStatus.LAPSED_MEMBER ||
    profile.memberStatus === API.MemberStatus.FORMER_MEMBER;
  const membershipIsMerged = profile.memberStatus === API.MemberStatus.MERGED;
  const mergedAccountMessage = profile.mergedPrimaryEmail
    ? t("messages.mergedAccount.withEmail", {
        email: profile.mergedPrimaryEmail,
      })
    : t("messages.mergedAccount.default");
  const membershipStatus = membershipStatusIsGoodStanding
    ? t("dashboard.membership.status.goodStanding")
    : ({
        [API.MemberStatus.MEMBER]: t("dashboard.membership.status.unknown"),
        [API.MemberStatus.LAPSED_MEMBER]: t("dashboard.membership.status.lapsed"),
        [API.MemberStatus.FORMER_MEMBER]: t("dashboard.membership.status.former"),
        [API.MemberStatus.DECEASED]: t("dashboard.membership.status.former"),
        [API.MemberStatus.MERGED]: t("dashboard.membership.status.merged"),
        [API.MemberStatus.CONTRIBUTOR]: t("dashboard.membership.status.unknown"),
        [API.MemberStatus.RES_33_EXPELLED]: t("dashboard.membership.status.unknown"),
        [API.MemberStatus.RES_33_RESIGNED]: t("dashboard.membership.status.unknown"),
        [API.MemberStatus.SUBSCRIBER]: t("dashboard.membership.status.unknown"),
      }[profile.memberStatus] ?? t("dashboard.membership.status.unknown"));
  const unavailable = t("dashboard.unavailable");

  return {
    billingName:
      [profile.billingFirstName, profile.billingLastName]
        .filter(Boolean)
        .join(" ")
        .trim() || unavailable,
    billingDetails: addressLines(profile.billingAddress),
    chapter: profile.chapter ?? unavailable,
    duesDetail: profile.dues?.display ?? unavailable,
    email: profile.email,
    // The backend already formats the card expiration as "MM/YY", so pass it
    // through as-is rather than running it through a date formatter.
    cardExpiration: profile.cardExpDate ?? null,
    joined: joinedDate
      ? t("dashboard.membership.memberSince", { date: joinedDate })
      : null,
    mailingDetails: addressLines(profile.mailingAddress),
    memberStatus: profile.memberStatus,
    mergedAccountMessage,
    membershipIsMerged,
    membershipProofActionHref: membershipStatusIsGoodStanding
      ? routes.external.proof
      : membershipIsRenewable
        ? routes.external.renew
        : null,
    membershipStatus,
    membershipStatusIcon,
    membershipStatusIsGoodStanding,
    name,
    nextPaymentDate: profile.nextPaymentDate
      ? formatLongDate(profile.nextPaymentDate, language)
      : unavailable,
    paymentMethod: profile.paymentMethod ?? unavailable,
    phone: mobilePhoneNumber(profile),
    profileActionLabel: membershipIsMerged
      ? t("dashboard.membership.actions.viewDetails")
      : t("dashboard.actions.update"),
    //TODO: https://github.com/dsausa/mydsa/issues/316
    profileImageSrc: null,
    proofActionLabel: membershipStatusIsGoodStanding
      ? t("dashboard.membership.actions.sendProof")
      : t("dashboard.membership.actions.renew"),
  };
}
